/**
 * SyncManager — controls when reference data is validated (not every mount).
 */

import { ReferenceDataStore } from './ReferenceDataStore.js';

let started = false;
let lastSyncAt = 0;
const MIN_SYNC_INTERVAL_MS = 60_000;

export const SyncManager = {
    async ensureCoreReference({ force = false } = {}) {
        // Users + pipelines only — shared by Deals/Accounts/Contacts/Commissions.
        // Goal types are Goals-module scoped (see ensureGoalTypes).
        await Promise.all([
            ReferenceDataStore.ensureUsersLookup({ force }),
            ReferenceDataStore.ensurePipelines({ force }),
        ]);
    },

    async ensureGoalTypes({ force = false } = {}) {
        try {
            const { ensureGoalTypesLoaded } = await import('../constants/GoalRegistry.js');
            await ensureGoalTypesLoaded({ force });
        } catch {
            /* optional — local GOAL_REGISTRY fallback */
        }
    },

    async revalidateIfDue({ force = false } = {}) {
        const now = Date.now();
        if (!force && now - lastSyncAt < MIN_SYNC_INTERVAL_MS) return null;
        lastSyncAt = now;
        try {
            return await ReferenceDataStore.revalidateFromSyncState();
        } catch (err) {
            console.warn('[SyncManager] sync-state failed', err);
            return null;
        }
    },

    start() {
        if (started || typeof window === 'undefined') return;
        started = true;

        const run = () => {
            void SyncManager.revalidateIfDue();
        };

        window.addEventListener('focus', run);
        window.addEventListener('authChanged', () => {
            ReferenceDataStore.clear();
            lastSyncAt = 0;
        });
        window.addEventListener('userSaved', () => {
            lastSyncAt = 0;
            void ReferenceDataStore.ensureUsersLookup({ force: true });
            void ReferenceDataStore.ensureUsersAssignable({ force: true });
        });
        window.addEventListener('userDeleted', () => {
            lastSyncAt = 0;
            void ReferenceDataStore.ensureUsersLookup({ force: true });
            void ReferenceDataStore.ensureUsersAssignable({ force: true });
        });
        window.addEventListener('pipelineCreated', () => {
            lastSyncAt = 0;
            void ReferenceDataStore.ensurePipelines({ force: true });
        });
        window.addEventListener('pipelineDeleted', () => {
            lastSyncAt = 0;
            void ReferenceDataStore.ensurePipelines({ force: true });
        });

        // Soft startup validation after first paint
        setTimeout(() => void SyncManager.revalidateIfDue(), 1500);
    },
};

if (typeof window !== 'undefined') {
    SyncManager.start();
}
